/**
 * Bill services — the tile every customer taps to pay a bill.
 *
 * ## What an operator actually owns here
 *
 * Not the catalogue. Which billers exist, what they are called, what they cost and what
 * identifier they take are all upstream facts, synced from the bill provider; a console that
 * let somebody rename MTN Airtime would be a console whose list drifts from the rail that has
 * to fulfil it. What is genuinely an operator decision is **how a service looks and whether it
 * is offered**:
 *
 *   - the **icon**, where somebody has uploaded one;
 *   - the **letter mark** and the **colour** behind it, which is what is drawn when nobody has
 *     — and that is every service today, so this is the common path rather than the fallback;
 *   - **availability**, which greys the tile in the app when a biller is down.
 *
 * ## Why the tile is drawn here at the size the app draws it
 *
 * The whole judgement this page exists for is "does that read as MTN at 28 points on a phone".
 * That cannot be made from a hex field and a two-character input, so the preview is the real
 * thing: the same box, the same corner radius, the same auto-shrinking label, and the text
 * colour picked by the same luminance rule the app uses. An operator who has to imagine the
 * result will pick a yellow that is unreadable in the grid and find out from a support ticket.
 *
 * ## Raster only, and the console says why
 *
 * An SVG is an active document — it can carry script and external references and it executes
 * when it is displayed — and these bytes are served from a public route to every customer's
 * phone and rendered in this console. So PNG, JPG and WebP, under 200 KB, checked twice: the
 * declared type first, then the **magic number**, because `File.type` is the browser's guess
 * from the extension and renaming `payload.svg` to `payload.png` changes it. The API sniffs
 * and refuses again; this only turns a rejection after a megabyte on the wire into an
 * immediate sentence naming what the file actually is.
 *
 * ## Permissions
 *
 * Superadmin, on the read as well as the writes — `admins:write` in the console's mirror.
 *
 * That is what the API enforces on all four of its bill-service routes: the list, the patch,
 * the icon upload and the icon read are every one of them behind `requireSuperadmin`, for the
 * same reason the banners and the brand assets are. These tiles are operator-authored artwork
 * and copy landing on every customer's Bills tab, and the bytes go into the same store the
 * console logo does.
 *
 * This page used to claim `providers:read` on the read and `rates:write` on the writes, and
 * both were wrong about the server. Every role holds `providers:read`, so support, compliance
 * and finance were all shown the sidebar entry and met a permission error the moment the list
 * loaded; finance was additionally shown edit controls the API would refuse.
 */
import { useMemo, useState } from 'react';

import { API_ORIGIN, USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import { API_PREFIX } from '../contracts/routes.js';
import type { BillService } from '../contracts/types.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  LoadingState,
  SearchInput,
  Select,
  Switch,
} from '../components/ui.js';
import { IconImage } from '../components/icons.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import {
  BILL_ICON_ACCEPT,
  BILL_ICON_MAX_BYTES,
  BILL_ICON_TYPE_LABEL,
  BILL_MARK_MAX,
  billMarkColor,
  checkBillColor,
  checkBillIconBytes,
  checkBillIconFile,
  checkBillMark,
  normaliseBillColor,
} from '../lib/billService.js';
import { formatMoney } from '../lib/money.js';
import { humanize } from '../lib/format.js';

/** What the operator is looking at. `no-icon` is the one that matches the actual job. */
type Filter = 'all' | 'no-icon' | 'available' | 'unavailable';

const FILTER_LABELS: Readonly<Record<Filter, string>> = {
  all: 'All services',
  'no-icon': 'Drawn from the mark',
  available: 'Offered in the app',
  unavailable: 'Switched off',
};

function matchesFilter(service: BillService, filter: Filter): boolean {
  switch (filter) {
    case 'no-icon':
      return service.iconPath === null;
    case 'available':
      return service.available;
    case 'unavailable':
      return !service.available;
    default:
      return true;
  }
}

/**
 * The absolute URL for a stored icon.
 *
 * The API sends an API-relative path because only the client knows which origin and prefix it
 * is talking to. Against the in-memory demo backend there are no stored bytes to point at, so
 * the row falls back to its mark chip — which is the state every real deployment is in today
 * anyway, and the one this page exists to make good.
 */
function iconSrc(service: BillService): string | null {
  if (USING_MOCK || service.iconPath === null) return null;
  return service.iconPath.startsWith(API_PREFIX)
    ? `${API_ORIGIN}${service.iconPath}`
    : `${API_ORIGIN}${API_PREFIX}${service.iconPath}`;
}

/** Read a File as bare base64 (no `data:` prefix) and as bytes, for the upload and the sniff. */
async function readFile(file: File): Promise<{ base64: string; bytes: Uint8Array }> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { base64: btoa(binary), bytes };
}

export function BillServicesPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  // `admins:write`, which is the console's name for superadmin, because that is what the API
  // gates the patch and the icon upload on. See the note on permissions at the top of the file.
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');
  const state = useAsync(() => api.listBillServices(), []);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<BillService | null>(null);

  // Memoised rather than `?? []` inline: a fresh empty array every render makes it a new
  // dependency every render, so the filter below would re-run on each keystroke.
  const services = useMemo(() => state.data?.services ?? [], [state.data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return services.filter(
      (s) =>
        matchesFilter(s, filter) &&
        (q === '' || s.name.toLowerCase().includes(q) || s.mark.toLowerCase().includes(q)),
    );
  }, [services, query, filter]);

  /** Replace one row in place, so a save does not reload and reshuffle the whole table. */
  function replace(updated: BillService): void {
    state.setData((prev) =>
      prev === null
        ? prev
        : { ...prev, services: prev.services.map((s) => (s.id === updated.id ? updated : s)) },
    );
  }

  /**
   * Switch a biller on or off.
   *
   * Not gathered into the editor with the mark and the colour, and deliberately: this is the
   * control an operator reaches for in a hurry, at the moment a biller starts refusing
   * payments. A switch that costs a dialog is a switch that gets flipped a minute late.
   */
  async function setAvailable(service: BillService, next: boolean): Promise<void> {
    setBusyId(service.id);
    try {
      const updated = await api.updateBillService(service.id, { available: next });
      replace(updated);
      toast.success(
        next ? 'Switched on' : 'Switched off',
        next
          ? `${updated.name} is offered in the app again.`
          : `${updated.name} is greyed out in the app. Payments to it are refused before they are taken.`,
      );
    } catch (err) {
      toast.error('Could not change that', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  async function uploadIcon(service: BillService, file: File | undefined): Promise<void> {
    if (file === undefined) return;

    const declared = checkBillIconFile(file);
    if (!declared.ok) {
      toast.error('That file cannot be used', declared.error);
      return;
    }

    setBusyId(service.id);
    try {
      const { base64, bytes } = await readFile(file);
      // The magic number, after the declared type. See the note at the top of this file.
      const sniffed = checkBillIconBytes(bytes, file.type);
      if (!sniffed.ok) {
        toast.error('That file cannot be used', sniffed.error);
        return;
      }
      const updated = await api.uploadBillServiceIcon(service.id, {
        mime: file.type as Parameters<typeof api.uploadBillServiceIcon>[1]['mime'],
        // `data`, not `dataBase64`: the bill-icon body spells it differently from the brand
        // and avatar uploads, which is a wart in the contract rather than here.
        data: base64,
      });
      replace(updated);
      toast.success('Icon uploaded', `${updated.name} now shows its own artwork in the app.`);
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading bill services…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const offered = services.filter((s) => s.available).length;
  const withoutIcon = services.filter((s) => s.iconPath === null).length;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Bill services</h1>
        <p className="page-head__sub">
          What customers see on the Bills tab. The list syncs from your bill provider; what you control is each tile's artwork and whether it is offered at all.
          {!canWrite && ' Your role can view these but not change them.'}
        </p>
      </div>

      <div className="grid grid--3" style={{ marginBottom: 16 }}>
        <Card title="Offered in the app">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{offered}</div>
          <div className="faint" style={{ fontSize: 12 }}>
            {services.length - offered} switched off and greyed out
          </div>
        </Card>
        <Card title="Drawn from the mark">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{withoutIcon}</div>
          <div className="faint" style={{ fontSize: 12 }}>
            No artwork uploaded, so the letter chip is what customers see
          </div>
        </Card>
        {/* The whole catalogue, switched-off services included — which is why it cannot be
            called "available": the first card already counts the available ones, and two cards
            claiming availability with different numbers is a board nobody can read. */}
        <Card title="In the catalogue">
          <div style={{ fontSize: 26, fontWeight: 650 }}>{services.length}</div>
          <div className="faint" style={{ fontSize: 12 }}>Synced from the bill provider</div>
        </Card>
      </div>

      <div className="toolbar">
        <div className="toolbar__grow">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search by name or mark…"
          />
        </div>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          aria-label="Filter services"
        >
          {(Object.keys(FILTER_LABELS) as Filter[]).map((key) => (
            <option key={key} value={key}>
              {FILTER_LABELS[key]}
            </option>
          ))}
        </Select>
      </div>

      <Card flush>
        {filtered.length === 0 ? (
          <EmptyState
            glyph="🧾"
            title="No services match"
            hint="Adjust the search or the filter above."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Tile</th>
                  <th>Service</th>
                  <th>Takes</th>
                  <th className="table__num">Range</th>
                  <th>Offered</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((service) => (
                  <tr key={service.id}>
                    <td>
                      <ServiceTile service={service} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{service.name}</div>
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {service.iconPath === null ? (
                          <>
                            Drawn from <span className="mono">{service.mark}</span> on{' '}
                            <span className="mono">{service.color}</span>
                          </>
                        ) : (
                          'Showing uploaded artwork'
                        )}
                      </div>
                    </td>
                    <td>
                      <div>{service.identifierLabel}</div>
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {humanize(service.identifierKind)}
                      </div>
                    </td>
                    <td className="table__num mono" style={{ fontSize: 12 }}>
                      {formatMoney(service.minAmount)} – {formatMoney(service.maxAmount)}
                    </td>
                    <td>
                      {canWrite ? (
                        <Switch
                          checked={service.available}
                          disabled={busyId === service.id}
                          onChange={(next) => void setAvailable(service, next)}
                          label={`Offer ${service.name}`}
                        />
                      ) : (
                        <Badge tone={service.available ? 'green' : 'neutral'} dot>
                          {service.available ? 'Offered' : 'Off'}
                        </Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {canWrite && (
                        <>
                          {/*
                            A label wrapping a hidden input, so the operator gets a real button
                            rather than the browser's default file control — which is a
                            different shape and a different colour in every browser and is the
                            one element on this page that would look like it belongs elsewhere.
                          */}
                          <label className="btn btn--sm btn--ghost" style={{ cursor: 'pointer' }}>
                            <IconImage size={14} />
                            {service.iconPath === null ? 'Upload icon' : 'Replace'}
                            <input
                              type="file"
                              accept={BILL_ICON_ACCEPT}
                              style={{ display: 'none' }}
                              disabled={busyId === service.id}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                // Cleared so choosing the same file twice fires again — after a
                                // failed upload that is exactly what an operator does next.
                                e.target.value = '';
                                void uploadIcon(service, file);
                              }}
                            />
                          </label>{' '}
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditing(service)}>
                        {canWrite ? 'Edit tile' : 'Details'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>
        {BILL_ICON_TYPE_LABEL} only, under {Math.round(BILL_ICON_MAX_BYTES / 1000)} KB. An icon is
        drawn at 28 points in the app&rsquo;s grid, so anything larger is bytes on a customer&rsquo;s
        data plan for pixels nobody sees. SVG is refused: it is a document that runs when it is
        displayed, and this artwork is served to every customer&rsquo;s phone.
      </p>

      {editing !== null && (
        <TileEditor
          // Keyed on the service so switching rows resets the draft rather than carrying one
          // service's half-typed mark onto another.
          key={editing.id}
          service={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            replace(updated);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/**
 * The tile, at the size and shape the app draws it.
 *
 * The uploaded artwork where there is any, and the typeset chip where there is not — which is
 * the same order `ServiceIcon` uses on the phone, so the console and the app cannot show a
 * different thing for one service. `allowFontScaling` has no meaning in a browser; the
 * equivalent here is the fixed box with the label shrunk to fit inside it, so a six-character
 * mark cannot grow past its own edges.
 */
function ServiceTile({
  service,
  size = 40,
}: {
  readonly service: BillService;
  readonly size?: number;
}): JSX.Element {
  const src = iconSrc(service);
  const [failed, setFailed] = useState(false);

  if (src !== null && !failed) {
    return (
      <span
        style={{
          display: 'inline-grid',
          placeItems: 'center',
          width: size,
          height: size,
          borderRadius: Math.round(size * 0.3),
          background: 'var(--bg-sunken)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        <img
          src={src}
          alt=""
          onError={() => setFailed(true)}
          style={{ width: size * 0.74, height: size * 0.74, objectFit: 'contain' }}
        />
      </span>
    );
  }

  return <MarkChip mark={service.mark} color={service.color} size={size} />;
}

/** The typeset chip: the mark, shrunk to fit, on the operator's colour. */
function MarkChip({
  mark,
  color,
  size,
}: {
  readonly mark: string;
  readonly color: string;
  readonly size: number;
}): JSX.Element {
  const background = normaliseBillColor(color) ?? '#111111';
  return (
    <span
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.3),
        background,
        color: billMarkColor(background),
        fontWeight: 700,
        // Shrinks with the mark's length rather than overflowing: `DStv` at the size `IE` is
        // drawn would push past the box, which is precisely the failure the app's own
        // `adjustsFontSizeToFit` prevents on the phone.
        fontSize: Math.max(8, Math.round((size * 0.62) / Math.max(2, [...mark].length))),
        letterSpacing: '-0.01em',
        lineHeight: 1,
        overflow: 'hidden',
      }}
      aria-hidden
    >
      {mark}
    </span>
  );
}

/**
 * The mark and the colour, with a live preview of what they produce.
 *
 * Both are drafts until Save, and both are validated as they are typed rather than on submit:
 * an operator picking a colour is iterating, and a form that only objects at the end makes
 * them do the iteration twice.
 */
function TileEditor({
  service,
  canWrite,
  onClose,
  onSaved,
}: {
  readonly service: BillService;
  readonly canWrite: boolean;
  readonly onClose: () => void;
  readonly onSaved: (updated: BillService) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [mark, setMark] = useState(service.mark);
  const [color, setColor] = useState(service.color);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const markCheck = checkBillMark(mark);
  const colorCheck = checkBillColor(color);
  const normalised = normaliseBillColor(color);
  const changed = mark.trim() !== service.mark || (normalised ?? '') !== service.color;
  const canSave = canWrite && markCheck.ok && colorCheck.ok && changed && !saving;

  async function save(): Promise<void> {
    if (!canSave || normalised === null) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateBillService(service.id, {
        mark: mark.trim(),
        color: normalised,
      });
      toast.success('Tile updated', `${updated.name} is drawn from the new mark and colour.`);
      onSaved(updated);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      title={service.name}
      description="The letter mark and the colour behind it. This is what customers see wherever no icon has been uploaded, which today is most services."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={!canSave} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      {/*
        The preview first, at the two sizes that matter: the grid tile on the Bills tab and the
        larger mark on the service's own screen. Judging a colour from a hex string is not a
        judgement anybody can make, and this page exists for exactly that judgement.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-sunken)',
          marginBottom: 'var(--space-5)',
        }}
      >
        <MarkChip mark={mark.trim() === '' ? service.mark : mark.trim()} color={color} size={28} />
        <MarkChip mark={mark.trim() === '' ? service.mark : mark.trim()} color={color} size={48} />
        <div className="faint" style={{ fontSize: 12, lineHeight: 1.5 }}>
          As drawn on the Bills grid and on the service&rsquo;s own screen. The text colour is
          picked automatically, black or white, by whichever can actually be read against your
          colour.
        </div>
      </div>

      <Field label={`Mark (up to ${String(BILL_MARK_MAX)} characters)`} htmlFor="bill-mark">
        <Input
          id="bill-mark"
          value={mark}
          maxLength={BILL_MARK_MAX}
          disabled={!canWrite || saving}
          onChange={(e) => setMark(e.target.value)}
        />
      </Field>
      {!markCheck.ok && <p className="adjust__bad">{markCheck.error}</p>}

      <Field label="Colour" htmlFor="bill-color">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {/*
            A native colour input beside the text one, both driving the same value. The picker
            is how somebody chooses; the hex field is how somebody pastes a brand colour out of
            a style guide, which is the more common of the two here.
          */}
          <input
            type="color"
            aria-label="Pick a colour"
            value={normalised ?? '#000000'}
            disabled={!canWrite || saving}
            onChange={(e) => setColor(e.target.value.toUpperCase())}
            style={{
              width: 38,
              height: 34,
              padding: 0,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'none',
              cursor: 'pointer',
            }}
          />
          <Input
            id="bill-color"
            className="mono"
            value={color}
            maxLength={7}
            disabled={!canWrite || saving}
            onChange={(e) => setColor(e.target.value)}
          />
        </div>
      </Field>
      {!colorCheck.ok && <p className="adjust__bad">{colorCheck.error}</p>}

      {error !== null && <ErrorBox message={error} />}

      <p className="muted" style={{ marginTop: 'var(--space-4)', fontSize: 12, lineHeight: 1.6 }}>
        Changing these changes what every customer sees on their next app launch. The catalogue
        itself comes from the bill provider and is not editable here: the name, the amounts and
        the identifier are all theirs.
      </p>
    </Modal>
  );
}
